import { Button, Card, Icon, KpiTile, Notice, RowMeter, ShareBar, formatMoney, formatNumber, formatPercent } from "@omnabase/ui";
import { change, type AiRead, type BandRow } from "@omnabase/shared";
import { useFetch } from "../../lib/inbox";
import { useNav, useNavigate, useWs } from "../../lib/nav";
import { NothingYet, ReportLoading, versus, type ReportContext } from "./ReportsScreen";

/** AI performance (brief In scope 1): what the AI did on its own, what people made of its drafts, the confidence bands against the
 *  threshold with the reading whether to trust it with more, the spot checks, the model spend for people with `aiSettings` only
 *  (the API leaves the card out for everyone else), and the knowledge base suggestions the AI report points at. */
const bandLabel = (b: number) => (b === 90 ? "90–100 %" : `${b}–${b + 9} %`);
const rate = (part: number, whole: number) => (whole > 0 ? part / whole : 0);

export function ReportAi({ ctx }: { ctx: ReportContext }) {
  const nav = useNav(); const navigate = useNavigate(); const { ws } = useWs();
  const r = useFetch<AiRead>(`/api/v1/w/${ctx.slug}/reports/ai?${ctx.query}`, [ctx.query]);
  if (!r.data) return r.error ? <p className="ob-report__note">{r.error}</p> : <ReportLoading />;
  const d = r.data; const c = d.current, p = d.previous;
  if (c.received === 0 && p.received === 0) return <NothingYet ctx={ctx} />;
  const settingsHref = ctx.project ? ws(`/settings/projects/${ctx.project.id}?tab=ai`) : ws("/settings/ai");
  const kb = ws(ctx.project ? `/knowledge/suggestions?project=${ctx.project.id}` : "/knowledge");
  const checked = d.spotChecks.checked, wrong = d.spotChecks.wrong;
  return (
    <>
      <div className="ob-kpis">
        <KpiTile value={formatPercent(c.aiShare)} label="Answered by the AI alone" sub={`${formatNumber(c.aiAlone)} replies · ${change(c.aiShare, p.aiShare, "points") ?? "new"}`} />
        <KpiTile value={c.reviewed ? formatPercent(c.asWrittenRate) : "—"} label="Drafts sent as written" sub={versus(change(c.asWrittenRate, p.asWrittenRate, "points"), ctx.range.days)} />
        <KpiTile value={checked ? formatPercent(1 - wrong / checked) : "—"} label="Right when spot-checked" sub={checked ? `${formatNumber(wrong)} of ${formatNumber(checked)} checked were wrong` : "Nothing spot-checked yet"} />
        <KpiTile value={formatNumber(c.reviewed)} unit="drafts" label="Decided on by people" sub={`${formatNumber(c.noDraft)} tickets had no draft`} />
      </div>
      {d.cost && (
        <Notice icon="card" action={<Button size="sm" onClick={() => navigate(settingsHref)}>AI settings</Button>}>
          <strong>{formatMoney(d.cost.eur, "EUR")}</strong> of model spend in this period, for every draft, classification and investigation. The workspace pays for the AI in credits, not by this figure: one credit per reply sent from an AI draft, and from one credit per code investigation, by its investigation effort.
        </Notice>
      )}
      <Card title="What became of every ticket">
        <ShareBar parts={[
          { id: "ai", label: "Answered by the AI alone", value: c.aiAlone, tone: "ai" },
          { id: "unchanged", label: "Draft sent as written", value: c.asWritten, tone: "strong" },
          { id: "edited", label: "Draft edited, then sent", value: c.edited, tone: "mid" },
          { id: "discarded", label: "Draft discarded, written by hand", value: c.discardedThenWritten, tone: "soft" },
          { id: "none", label: "No draft", value: c.noDraft, tone: "faint" },
        ]} />
      </Card>
      <Card title="Confidence against what people did" className="ob-card--table">
        <div className="ob-dtable-card">
          <table className="ob-dtable">
            <thead><tr><th>Confidence</th><th className="ob-dtable__num">Drafts</th><th>As written</th><th className="ob-dtable__num ob-dtable__col--phone">Edited</th><th className="ob-dtable__num ob-dtable__col--phone">Discarded</th><th className="ob-dtable__col--phone"></th></tr></thead>
            <tbody>
              {d.bands.map((b) => {
                const reviewed = b.asWritten + b.edited + b.discardedThenWritten;
                return (
                  <tr key={b.band} className={b.holdsThreshold ? "ob-dtable__row--marked" : undefined}>
                    <td className="ob-dtable__name">{bandLabel(b.band)}{b.holdsThreshold && d.setting.autoSend ? " · the threshold" : ""}</td>
                    <td className="ob-dtable__num">{formatNumber(b.drafts)}</td>
                    <td><span className="ob-metercell"><RowMeter value={rate(b.asWritten, reviewed)} tone={b.auto ? "ai" : undefined} /><span className="ob-metercell__value">{reviewed ? formatPercent(rate(b.asWritten, reviewed)) : "—"}</span></span></td>
                    <td className="ob-dtable__num ob-dtable__col--phone">{reviewed ? formatPercent(rate(b.edited, reviewed)) : "—"}</td>
                    <td className="ob-dtable__num ob-dtable__col--phone">{reviewed ? formatPercent(rate(b.discardedThenWritten, reviewed)) : "—"}</td>
                    <td className="ob-dtable__muted ob-dtable__col--phone">{b.aiAlone > 0 ? `${formatNumber(b.aiAlone)} went out on their own` : b.auto ? "goes out on its own" : "waits for a person"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <Reading d={d} />
      </Card>
      <div className="ob-grid-2 ob-grid-2--even">
        <Card title="Where the knowledge base can help">
          <p className="ob-report__note">{c.noDraft > 0 ? `${formatNumber(c.noDraft)} tickets of the period got no draft at all, and ${formatNumber(c.discardedThenWritten)} had theirs thrown away: the questions the sources do not answer yet.` : "Every ticket of the period got a draft."}</p>
          {d.suggestions > 0 && <a className="ob-report__more" href={kb} onClick={(e) => nav(kb, e)}>{d.suggestions} knowledge base suggestions would cover questions like these<Icon name="arrowForward" size="sm" /></a>}
        </Card>
        {d.byProject && (
          <Card title="By project" className="ob-card--table">
            <div className="ob-dtable-card">
              <table className="ob-dtable">
                <thead><tr><th>Project</th><th className="ob-dtable__num">By the AI alone</th><th className="ob-dtable__num">As written</th><th className="ob-dtable__num ob-dtable__col--phone">Wrong</th></tr></thead>
                <tbody>
                  {d.byProject.map((pr) => {
                    const to = ctx.link("/reports/ai", pr.projectId);
                    return (
                      <tr key={pr.projectId}>
                        <td className="ob-dtable__grow"><a className="ob-dtable__name" href={to} onClick={(e) => nav(to, e)} title={pr.name}>{pr.name}</a></td>
                        <td className="ob-dtable__num">{formatPercent(pr.aiShare)}</td>
                        <td className="ob-dtable__num">{formatPercent(pr.asWrittenRate)}</td>
                        <td className="ob-dtable__num ob-dtable__col--phone">{pr.checked ? `${pr.wrong} of ${pr.checked}` : "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>
    </>
  );
}

/** the trust question: where the threshold sits, and what the next band down would have done */
/** the reading under the bands (AI settings round one, 2026-10-18: auto send left the product, so the reading no longer offers a threshold to
 *  change or counts what sending a band automatically would have answered; it says how people treated the next band's drafts) */
function Reading({ d }: { d: AiRead }) {
  const next: BandRow | null = d.next;
  const nextReviewed = next ? next.asWritten + next.edited + next.discardedThenWritten : 0;
  return (
    <p className="ob-report__reading">
      <Icon name="sparkles" size="sm" />
      <span>
        Nothing goes out on its own here: every draft waits for a person.{" "}
        {next && <>In the {bandLabel(next.band)} band people sent <strong>{nextReviewed ? formatPercent(rate(next.asWritten, nextReviewed)) : "none"}</strong> of <strong>{formatNumber(next.drafts)}</strong> drafts as written.</>}
      </span>
    </p>
  );
}
